import { useEffect, useRef, useState } from "react";
import { AiServiceError } from "utils/aiServiceRequest";
import {
  completeMcpFlow,
  getMcpFlowInfo,
  McpFlowAction,
  McpFlowInfo,
} from "./mcpAuthorize.api";
import McpConsentCard from "./McpConsentCard";
import McpMessageCard from "./McpMessageCard";

type FlowState =
  | { status: "loading" }
  | { status: "ready"; info: McpFlowInfo }
  | { status: "expired" }
  | { status: "error" };

const EXPIRED_MESSAGE =
  "This authorization request has expired. Return to your MCP client and try again.";

function submitErrorMessage(error: unknown): string {
  if (error instanceof AiServiceError && error.status === 401) {
    return "We couldn't verify your account for this connection. Reload this page and sign in again, or use a supported account.";
  }
  if (error instanceof AiServiceError && error.status === 400) {
    return EXPIRED_MESSAGE;
  }
  return "Something went wrong. Please try again.";
}

export default function McpConsent({ flowId }: { flowId: string }) {
  const [flow, setFlow] = useState<FlowState>({ status: "loading" });
  const [submittingAction, setSubmittingAction] =
    useState<McpFlowAction | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // State updates are async; the ref makes a double-click a no-op immediately.
  const inFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getMcpFlowInfo(flowId)
      .then((info) => {
        if (!cancelled) setFlow({ status: "ready", info });
      })
      .catch((error) => {
        if (cancelled) return;
        setFlow(
          error instanceof AiServiceError && error.status === 404
            ? { status: "expired" }
            : { status: "error" }
        );
      });
    return () => {
      cancelled = true;
    };
  }, [flowId]);

  const respond = async (action: McpFlowAction) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmittingAction(action);
    setSubmitError(null);
    try {
      const { redirectUrl } = await completeMcpFlow(flowId, action);
      window.location.assign(redirectUrl);
    } catch (error) {
      inFlight.current = false;
      setSubmittingAction(null);
      setSubmitError(submitErrorMessage(error));
    }
  };

  if (flow.status === "loading") {
    return (
      <McpMessageCard title="Connecting…" message="Loading your request." />
    );
  }
  if (flow.status === "expired") {
    return <McpMessageCard title="Request expired" message={EXPIRED_MESSAGE} />;
  }
  if (flow.status === "error") {
    return (
      <McpMessageCard
        title="Something went wrong"
        message="We couldn't load this authorization request. Reload the page, or return to your MCP client and try again."
      />
    );
  }

  const { clientName, redirectHost } = flow.info;
  return (
    <McpConsentCard
      clientName={clientName}
      redirectHost={redirectHost}
      submittingAction={submittingAction}
      error={submitError}
      onAllow={() => respond("allow")}
      onDeny={() => respond("deny")}
    />
  );
}
